import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Icon } from "@iconify/react";
import useApi from "../../hook/useApi";
import { toast } from "react-toastify";
import Loader from "../Loader/Loader";
import Swal from "sweetalert2";
import { PAGE_SIZE_OPTIONS } from "../../constants/pageSizeOptions";
import SearchableSelect from "../Common/SearchableSelect";

const formatBalance = (balance) => {
  const n = Number(balance) || 0;
  const isNegative = n < 0;
  const isZero = n === 0;

  const formatted = Math.abs(n).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  return {
    display: `${isNegative ? "-" : ""}₹${formatted}`,
    className: isZero ? "text-secondary-600" : isNegative ? "text-danger-600" : "text-success-600",
  };
};

const SettlementManagementLayer = () => {
  const { AsyncGetApiCall, AsyncPostApiCall } = useApi();
  const [settling, setSettling] = useState(false);

  const [unsettledRows, setUnsettledRows] = useState([]);
  const [unsettledLoading, setUnsettledLoading] = useState(false);
  const [accountType, setAccountType] = useState("all");

  const fetchUnsettledBalances = useCallback(async () => {
    setUnsettledLoading(true);
    try {
      const response = await AsyncGetApiCall("/accounts/get-unsettled-balances");
      console.log("Unsettled API response:", response);
      if (response?.success) {
        setUnsettledRows(response.data || []);
      } else {
        toast.error(response?.message || "Failed to fetch unsettled balances");
        setUnsettledRows([]);
      }
    } catch (err) {
      console.error("fetchUnsettledBalances Error:", err);
      toast.error("An error occurred while fetching unsettled balances");
      setUnsettledRows([]);
    } finally {
      setUnsettledLoading(false);
    }
  }, [AsyncGetApiCall]);

  useEffect(() => {
    fetchUnsettledBalances();
  }, [fetchUnsettledBalances]);

  const handleManualSettlement = async () => {
    const today = new Date().toLocaleDateString("en-IN", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });

    const { value: confirmed } = await Swal.fire({
      title: "Confirm Global Settlement",
      text: `Do you want to run the weekly settlement for today (${today})? This will calculate ledgers across the entire system.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#2352c3",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, settle it!",
      customClass: { popup: "swal-sm" },
    });

    if (!confirmed) {
      return;
    }

    setSettling(true);
    try {
      const response = await AsyncPostApiCall("/accounts/settle-weekly", {});
      if (response?.success) {
        toast.success(response.message || "Settlement completed successfully");
        fetchUnsettledBalances(); // Refresh unsettled
      } else {
        toast.error(response?.message || "Failed to complete settlement");
      }
    } catch (error) {
      toast.error("Error occurred during manual settlement");
    } finally {
      setSettling(false);
    }
  };

  // Removed historical fetch logic

  const handleIndividualSettlement = async (row) => {
    const { value: confirmed } = await Swal.fire({
      title: "Confirm Settlement",
      text: `Are you sure you want to settle the amount of ₹${Math.abs(row.balance).toLocaleString("en-IN", { minimumFractionDigits: 2 })} for ${row.userName}?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#2352c3",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, settle it!",
      customClass: { popup: "swal-sm" },
    });

    if (!confirmed) {
      return;
    }

    setUnsettledLoading(true);
    try {
      const res = await AsyncPostApiCall("/accounts/settle-individual", {
        targetUserId: row.userId,
        targetUserType: row.userType,
      });

      if (res?.success) {
        toast.success(res.message || "Account settled successfully");
        fetchUnsettledBalances(); // Refresh list
      } else {
        toast.error(res?.message || "Failed to settle account");
      }
    } catch (error) {
      toast.error("Error occurred during individual settlement");
    } finally {
      setUnsettledLoading(false);
    }
  };

  const handleInvoice = async (row) => {
    try {
      if (!row.from || !row.to) {
        toast.warning("Date range missing for invoice");
        return;
      }
      setUnsettledLoading(true);
      const response = await AsyncGetApiCall(
        `/accounts/generate-invoice?from=${encodeURIComponent(row.from)}&to=${encodeURIComponent(row.to)}&targetUserId=${row.userId}`,
      );

      if (response?.success === false) {
        toast.error(response.message || "Failed to generate invoice");
      } else {
        const newWindow = window.open("", "_blank");
        if (newWindow) {
          newWindow.document.open();
          newWindow.document.write(response?.data || response);
          newWindow.document.close();
        }
      }
    } catch (error) {
      console.error("Failed to open invoice", error);
      toast.error("An error occurred while generating the invoice");
    } finally {
      setUnsettledLoading(false);
    }
  };

  return (
    <>
      {unsettledLoading && <Loader />}

      <div className="d-flex flex-column flex-lg-row align-items-start align-items-lg-center justify-content-between gap-3 mb-24">
        <div className="d-flex align-items-center gap-2 text-secondary-light">
          <Icon icon="tabler:layout-grid" className="icon text-xl" />
          <Icon icon="tabler:chevron-right" className="icon text-sm" />
          <span className="fw-semibold text-primary-light">Settlement Management</span>
        </div>
        
        {/* Manual Settlement Button for Admin only */}
        <div>
          <button 
            className="btn btn-warning d-flex align-items-center gap-2 shadow-sm px-20 py-10"
            onClick={handleManualSettlement}
            disabled={settling}
          >
            <Icon icon="tabler:cash" className="text-xl" />
            {settling ? "Processing Settlement..." : "Settle Weekly Accounts"}
          </button>
        </div>
      </div>

      {/* Unsettled Balances Section */}
      <div className="card border shadow-sm radius-16 mb-24">
        <div className="card-header bg-warning-50 border-bottom p-16 d-flex justify-content-between align-items-center">
          <h6 className="mb-0 text-warning-700 d-flex align-items-center gap-2">
            <Icon icon="tabler:alert-circle" className="text-xl" />
            Unsettled Balances (Pending Settlement)
          </h6>
          <span className="badge bg-warning-600 text-white px-12 py-6 rounded-pill">
            {unsettledRows.length} Accounts
          </span>
        </div>
        <div className="card-body p-0">
          <div className="table-responsive" style={{ maxHeight: '300px', overflowY: 'auto' }}>
            {unsettledLoading ? (
              <div className="p-24 text-center"><Loader /></div>
            ) : unsettledRows.length === 0 ? (
              <div className="p-24 text-center text-secondary-light fw-medium">All accounts are fully settled.</div>
            ) : (
              <table className="table bordered-table sm-table mb-0">
                <thead className="sticky-top bg-white">
                  <tr>
                    <th>Login ID</th>
                    <th>Name</th>
                    <th>Account Type</th>
                    <th>Unsettled Balance (Rs.)</th>
                    <th className="text-center">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {unsettledRows.map((row) => {
                    const { display, className } = formatBalance(row.balance);
                    return (
                      <tr key={row._id}>
                        <td className="text-secondary-light">{row.userLoginId}</td>
                        <td className="text-secondary-light fw-semibold">{row.userName}</td>
                        <td>
                          <span className="badge bg-primary-50 text-primary-600 text-capitalize">{row.userType}</span>
                        </td>
                        <td className={`fw-bold ${className}`}>{display}</td>
                        <td className="text-center">
                          <div className="d-flex align-items-center justify-content-center gap-2">
                            <button
                              type="button"
                              title="Settle Account"
                              className="bg-primary-focus text-primary-600 bg-hover-primary-200 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle border-0"
                              onClick={() => handleIndividualSettlement(row)}
                            >
                              <Icon icon="tabler:cash" className="text-lg" />
                            </button>
                            {row.userType !== 'admin' && (
                              <button
                                type="button"
                                title="Invoice"
                                className="bg-success-focus text-success-600 bg-hover-success-200 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle border-0"
                                onClick={() => handleInvoice(row)}
                              >
                                <Icon icon="tabler:file-text" className="text-lg" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>

      {/* The historical table has been removed per user request */}
    </>
  );
};

export default SettlementManagementLayer;
